/* eslint-disable @typescript-eslint/no-namespace */
import { functionSchema, type AbstractApiSchema } from "vue-utils"
import { Duration, Instant, Month, Now, PlainDate, PlainDateTime, PlainTime, ZonedDateTime } from "../temporal"

declare module "temporal-spec" {
	export namespace Temporal {
		export namespace Duration {
			let Schema: AbstractApiSchema<Duration>
		}
		export namespace Instant {
			let Schema: AbstractApiSchema<Instant>
		}
		export interface Instant {
			inUserTimeZone(): ZonedDateTime

			toGBDateString(options?: globalThis.Intl.DateTimeFormatOptions): string
			toGBDateTimeString(options?: globalThis.Intl.DateTimeFormatOptions): string

			isAfter(other: Instant): boolean
			isBefore(other: Instant): boolean
			compareTo(other: Instant): number
		}
		export namespace PlainDate {
			let Schema: AbstractApiSchema<PlainDate>
		}
		export interface PlainDate {
			toGBString(options?: globalThis.Intl.DateTimeFormatOptions): string
			getMonth(): Month

			isAfter(other: PlainDate): boolean
			isBefore(other: PlainDate): boolean
			compareTo(other: PlainDate): number
		}
		export namespace PlainTime {
			let Schema: AbstractApiSchema<PlainTime>
		}
		export interface PlainTime {
			toHTMLInput(): string
		}
		export namespace PlainDateTime {
			let Schema: AbstractApiSchema<PlainDateTime>
		}
		export interface PlainDateTime {
			toGBString(options?: globalThis.Intl.DateTimeFormatOptions): string
			getMonth(): Month

			isAfter(other: PlainDateTime): boolean
			isBefore(other: PlainDateTime): boolean
			compareTo(other: PlainDateTime): number
		}
		export namespace ZonedDateTime {
			let Schema: AbstractApiSchema<ZonedDateTime>
		}
		export interface ZonedDateTime {
			getMonth(): Month
		}
	}
}

/* Try dd/mm/yyyy formats first, then if not existant, use yyy/mm/dd formats, then FINALLY fall back to American english if all else fails */
const locales = ["en-GB", "en-IE", "en-AU", "en-NZ", "en-CA", "sv-SE", "en-US"]

Duration.Schema = functionSchema((text) => Duration.from(text))
Instant.Schema = functionSchema((text) => Instant.from(text))
PlainDate.Schema = functionSchema((text) => PlainDate.from(text))
PlainTime.Schema = functionSchema((text) => PlainTime.from(text))
PlainDateTime.Schema = functionSchema((text) => PlainDateTime.from(text))
ZonedDateTime.Schema = functionSchema((text) => ZonedDateTime.from(text))

Instant.prototype.inUserTimeZone = function () {
	return this.toZonedDateTimeISO(Now.timeZoneId())
}
Instant.prototype.toGBDateString = function (options) {
	return this.inUserTimeZone().toPlainDate().toGBString(options)
}
Instant.prototype.toGBDateTimeString = function (options) {
	return this.inUserTimeZone().toPlainDateTime().toGBString(options)
}

Instant.prototype.isBefore = function (other) {
	return Instant.compare(this, other) < 0
}
Instant.prototype.isAfter = function (other) {
	return Instant.compare(this, other) > 0
}
Instant.prototype.compareTo = function (other) {
	return Instant.compare(this, other)
}

PlainDate.prototype.toGBString = function (options) {
	return this.toLocaleString(locales, options)
}
PlainDate.prototype.getMonth = function () {
	return Month.of(this.month)
}
PlainDate.prototype.isBefore = function (other) {
	return PlainDate.compare(this, other) < 0
}
PlainDate.prototype.isAfter = function (other) {
	return PlainDate.compare(this, other) > 0
}
PlainDate.prototype.compareTo = function (other) {
	return PlainDate.compare(this, other)
}

PlainTime.prototype.toHTMLInput = function () {
	return this.toString({ smallestUnit: "minute" })
}
PlainTime.prototype.toJSON = function () {
	return this.toString({ smallestUnit: "second" })
}

PlainDateTime.prototype.toJSON = function () {
	return this.toString({ smallestUnit: "second" })
}
PlainDateTime.prototype.getMonth = function () {
	return Month.of(this.month)
}
PlainDateTime.prototype.isBefore = function (other) {
	return PlainDateTime.compare(this, other) < 0
}
PlainDateTime.prototype.isAfter = function (other) {
	return PlainDateTime.compare(this, other) > 0
}
PlainDateTime.prototype.compareTo = function (other) {
	return PlainDateTime.compare(this, other)
}

PlainDateTime.prototype.toGBString = function (options) {
	return this.toLocaleString(
		locales,
		options ?? {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		}
	)
}

ZonedDateTime.prototype.getMonth = function () {
	return Month.of(this.month)
}
